import React,{useEffect,useState} from "react";
import {db} from "../../firebase/firebase.js";
import {collection,getDocs,orderBy,query} from "firebase/firestore";

export default function AdminDashboard(){
  const[loading,setLoading]=useState(true);
  const[workers,setWorkers]=useState([]);
  const[search,setSearch]=useState("");

  useEffect(()=>{
    const loadWorkers=async()=>{
      try{
        const q=query(
          collection(db,"businesses","axuremoval","quotes"),
          orderBy("createdAt","desc")
        );

        const snap=await getDocs(q);

        const data=snap.docs
          .map((doc)=>({id:doc.id,...doc.data()}))
          .filter((item)=>item.type==="worker_application");

        setWorkers(data);
      }catch(err){
        console.error("Failed to load workers:",err);
      }finally{
        setLoading(false);
      }
    };

    loadWorkers();
  },[]);

  const filteredWorkers=workers.filter((worker)=>{
    const text=`
      ${worker.name || ""}
      ${worker.email || ""}
      ${worker.phone || ""}
      ${worker.role || ""}
      ${worker.postcode || ""}
      ${worker.availability || ""}
      ${worker.rightToWork || ""}
    `.toLowerCase();

    return text.includes(search.toLowerCase());
  });

  if(loading){
    return(
      <div className="text-white p-10">
        Loading AXU Workforce dashboard...
      </div>
    );
  }

  return(
    <div className="text-white p-6 space-y-8">

      <div>
        <h1 className="text-3xl font-bold">AXU Workforce Dashboard</h1>
        <p className="text-neutral-400 mt-1">
          Manage worker applications, documents, and onboarding status.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Metric label="Total Workers" value={workers.length}/>
        <Metric label="New Applications" value={workers.filter((w)=>w.status==="new_application").length}/>
        <Metric label="Ready to Contact" value={workers.filter((w)=>w.email || w.phone).length}/>
        <Metric label="With Documents" value={workers.filter((w)=>w.idDocumentUrl || w.proofOfAddressUrl || w.rightToWorkDocumentUrl || w.certificateDocumentUrl).length}/>
      </div>

      <input
        value={search}
        onChange={(e)=>setSearch(e.target.value)}
        placeholder="Search by name, role, postcode, email, phone..."
        className="w-full bg-black border border-neutral-700 rounded-lg px-4 py-3 text-white outline-none"
      />

      <div className="bg-neutral-900 border border-neutral-800 rounded-xl overflow-hidden">
        {filteredWorkers.length > 0 ? (
          filteredWorkers.map((worker)=>(
            <WorkerCard key={worker.id} worker={worker}/>
          ))
        ) : (
          <p className="p-6 text-neutral-400">No workers found.</p>
        )}
      </div>

    </div>
  );
}

function Metric({label,value}){
  return(
    <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-5">
      <p className="text-sm text-neutral-400">{label}</p>
      <p className="text-3xl font-bold mt-2 text-cyan-400">{value}</p>
    </div>
  );
}

function WorkerCard({worker}){
  return(
    <div className="p-6 border-b border-neutral-800 space-y-4">

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">{worker.name || "Unnamed Worker"}</h2>
          <p className="text-neutral-400">{worker.role || "No role provided"}</p>
          <p className="text-neutral-500 text-sm">{worker.postcode || "No postcode"}</p>
        </div>

        <span className="bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full text-sm">
          {worker.status || "new_application"}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-neutral-300">
        <p><strong>Email:</strong> {worker.email || "Not provided"}</p>
        <p><strong>Phone:</strong> {worker.phone || "Not provided"}</p>
        <p><strong>Availability:</strong> {worker.availability || "Not provided"}</p>
        <p><strong>Right to Work:</strong> {worker.rightToWork || "Not provided"}</p>
        <p><strong>Driving Licence:</strong> {worker.drivingLicence || "Not provided"}</p>
        <p><strong>Own Vehicle:</strong> {worker.ownVehicle || "Not provided"}</p>
        <p><strong>NI Number:</strong> {worker.nationalInsurance || "Not provided"}</p>
        <p><strong>Bank:</strong> {worker.bankName || "Not provided"}</p>
        <p><strong>Sort Code:</strong> {worker.sortCode || "Not provided"}</p>
      </div>

      {worker.experience && (
        <p className="text-sm text-neutral-400">
          <strong>Experience:</strong> {worker.experience}
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        <DocumentLink label="ID Document" url={worker.idDocumentUrl}/>
        <DocumentLink label="Proof of Address" url={worker.proofOfAddressUrl}/>
        <DocumentLink label="Right to Work" url={worker.rightToWorkDocumentUrl}/>
        <DocumentLink label="Certificate" url={worker.certificateDocumentUrl}/>
      </div>

      <div className="flex flex-wrap gap-3 pt-2">
        {worker.email && (
          <a
            href={`mailto:${worker.email}`}
            className="bg-blue-600 px-4 py-2 rounded-lg text-sm font-semibold"
          >
            Email Worker
          </a>
        )}

        {worker.phone && (
          <a
            href={`tel:${worker.phone}`}
            className="bg-green-600 px-4 py-2 rounded-lg text-sm font-semibold"
          >
            Call Worker
          </a>
        )}

        {worker.phone && (
          <a
            href={`sms:${worker.phone}`}
            className="bg-cyan-600 px-4 py-2 rounded-lg text-sm font-semibold"
          >
            Text Worker
          </a>
        )}
      </div>

    </div>
  );
}

function DocumentLink({label,url}){
  if(!url) return null;

  return(
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="bg-neutral-800 border border-neutral-700 px-4 py-2 rounded-lg text-sm text-cyan-300"
    >
      View {label}
    </a>
  );
}